import qs from 'qs';
import { MapLayerType } from '../MapLayer.ts';
import { parseLayerAndOptions } from '../rendering/layerUrl.ts';
import {
  buildWfsCapabilitiesUrl,
  parseWfsCapabilities,
} from '../wfs/parseWfsCapabilities.ts';

export type ServiceDetectionStatus =
  /** Il servizio risponde in modo coerente con il tipo configurato */
  | 'match'
  /** Il servizio risponde, ma come un tipo diverso da quello configurato */
  | 'mismatch'
  /** URL non raggiungibile dal browser (rete, CORS, servizio spento) */
  | 'unreachable'
  /** Il servizio risponde ma il tipo non è riconoscibile */
  | 'unknown';

export interface IServiceDetectionResult {
  status: ServiceDetectionStatus;
  /** Tipo riconosciuto dalla risposta del servizio (match o mismatch) */
  detectedType?: MapLayerType;
  /** Etichetta con dettaglio del protocollo (es. 'WFS 2.0.0') */
  detectedLabel?: string;
}

/** Esito di una singola probe di protocollo. */
interface IProbeOutcome {
  /** Il servizio ha risposto (anche con un errore applicativo) */
  reachable: boolean;
  /**
   * Tipi di layer compatibili con la risposta. Più di uno quando la
   * risposta è ambigua (es. un MapServer tilizzato serve sia raster
   * che tile).
   */
  plausibleTypes: MapLayerType[];
  label?: string;
}

const UNREACHABLE_OUTCOME: IProbeOutcome = {
  reachable: false,
  plausibleTypes: [],
};

/**
 * Classifica la risposta `?f=json` di un endpoint ArcGIS REST.
 * ArcGIS risponde HTTP 200 anche in caso di errore (body `{error}`).
 */
export function classifyArcGisResponse(body: unknown): MapLayerType[] {
  if (typeof body !== 'object' || body === null) return [];
  const response = body as Record<string, unknown>;

  if (response.error) return [];

  // URL di un singolo layer: interrogabile come vettoriale
  if (Array.isArray(response.fields) || 'geometryType' in response) {
    return [MapLayerType.ArcGISRestVector];
  }

  // Root del servizio con cache tile: utilizzabile sia come tile che
  // come raster (export)
  if (response.tileInfo || response.singleFusedMapCache === true) {
    return [MapLayerType.ArcGISRestTile, MapLayerType.ArcGISRest];
  }

  // Root del servizio senza cache: solo export raster
  if (typeof response.mapName === 'string' || Array.isArray(response.layers)) {
    return [MapLayerType.ArcGISRest];
  }

  return [];
}

async function probeArcGisService(rawUrl: string): Promise<IProbeOutcome> {
  const { url, options } = parseLayerAndOptions(rawUrl);
  const query = qs.stringify({ ...options, f: 'json' });

  let response: Response;
  try {
    response = await fetch(`${url}?${query}`);
  } catch {
    return UNREACHABLE_OUTCOME;
  }
  if (!response.ok) return { reachable: true, plausibleTypes: [] };

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { reachable: true, plausibleTypes: [] };
  }

  return { reachable: true, plausibleTypes: classifyArcGisResponse(body) };
}

async function probeWfsService(rawUrl: string): Promise<IProbeOutcome> {
  let response: Response;
  try {
    response = await fetch(buildWfsCapabilitiesUrl(rawUrl));
  } catch {
    return UNREACHABLE_OUTCOME;
  }
  if (!response.ok) return { reachable: true, plausibleTypes: [] };

  try {
    const capabilities = parseWfsCapabilities(await response.text());
    return {
      reachable: true,
      plausibleTypes: [MapLayerType.WFS],
      label: `WFS ${capabilities.version}`,
    };
  } catch {
    return { reachable: true, plausibleTypes: [] };
  }
}

/** Root element del GetCapabilities WMS: 1.3.0 e 1.1.x differiscono. */
const WMS_CAPABILITIES_ROOTS = new Set([
  'WMS_Capabilities',
  'WMT_MS_Capabilities',
]);

async function probeWmsService(rawUrl: string): Promise<IProbeOutcome> {
  const { url, options } = parseLayerAndOptions(rawUrl);
  const query = qs.stringify({
    ...options,
    service: 'WMS',
    request: 'GetCapabilities',
  });

  let response: Response;
  try {
    response = await fetch(`${url}?${query}`);
  } catch {
    return UNREACHABLE_OUTCOME;
  }
  if (!response.ok) return { reachable: true, plausibleTypes: [] };

  try {
    const document = new DOMParser().parseFromString(
      await response.text(),
      'text/xml',
    );
    const root = document.documentElement;
    if (root && WMS_CAPABILITIES_ROOTS.has(root.localName)) {
      return {
        reachable: true,
        plausibleTypes: [MapLayerType.WMS],
        label: 'WMS',
      };
    }
  } catch {
    // risposta non XML: non è un WMS
  }

  return { reachable: true, plausibleTypes: [] };
}

type ProbeKey = 'arcgis' | 'wfs' | 'wms';

const DETECTION_PROBES: Record<
  ProbeKey,
  (url: string) => Promise<IProbeOutcome>
> = {
  arcgis: probeArcGisService,
  wfs: probeWfsService,
  wms: probeWmsService,
};

const PROBE_KEY_BY_TYPE: Partial<Record<MapLayerType, ProbeKey>> = {
  [MapLayerType.ArcGISRest]: 'arcgis',
  [MapLayerType.ArcGISRestTile]: 'arcgis',
  [MapLayerType.ArcGISRestVector]: 'arcgis',
  [MapLayerType.WMS]: 'wms',
  [MapLayerType.WFS]: 'wfs',
};

/**
 * True per i tipi il cui servizio è verificabile con una probe.
 * OSM e XYZ non hanno capabilities interrogabili.
 */
export function isDetectableLayerType(type: MapLayerType | undefined): boolean {
  return type !== undefined && PROBE_KEY_BY_TYPE[type] !== undefined;
}

/**
 * Verifica che il servizio all'URL risponda in modo coerente con il tipo
 * configurato. Prova prima il protocollo del tipo atteso (percorso
 * felice: una sola richiesta), poi gli altri per poter riportare come
 * risponde davvero il servizio in caso di mismatch.
 *
 * Non lancia mai: gli errori sono rappresentati nello status.
 */
export async function detectMapServiceType(
  url: string,
  expectedType: MapLayerType,
): Promise<IServiceDetectionResult> {
  const expectedProbe = PROBE_KEY_BY_TYPE[expectedType];
  if (!expectedProbe) return { status: 'unknown' };

  const probeOrder: ProbeKey[] = [
    expectedProbe,
    ...(Object.keys(DETECTION_PROBES) as ProbeKey[]).filter(
      key => key !== expectedProbe,
    ),
  ];

  let reachable = false;
  let detected: { type: MapLayerType; label?: string } | undefined;

  for (const key of probeOrder) {
    const outcome = await DETECTION_PROBES[key](url);
    reachable = reachable || outcome.reachable;

    // Risposta ambigua compatibile col tipo atteso → mai un falso
    // negativo su una config funzionante
    if (outcome.plausibleTypes.includes(expectedType)) {
      return {
        status: 'match',
        detectedType: expectedType,
        detectedLabel: outcome.label,
      };
    }

    if (!detected && outcome.plausibleTypes.length > 0) {
      detected = { type: outcome.plausibleTypes[0], label: outcome.label };
    }
  }

  if (detected) {
    return {
      status: 'mismatch',
      detectedType: detected.type,
      detectedLabel: detected.label,
    };
  }

  return { status: reachable ? 'unknown' : 'unreachable' };
}
